import { MOBILE_SHELL_SPRING_TRANSITION } from '@/constants/motion'
import { useIsMobileShellHidden } from '@/utils/dom'
import { useIsMobileTabBarLayout } from '@/utils/hooks/useMediaQuery'
import router from '@/utils/router'
import { Button } from '@mantine/core'
import { IconChevronLeft } from '@tabler/icons-react'
import { useCanGoBack } from '@tanstack/react-router'
import clsx from 'clsx'
import { HTMLMotionProps, motion, useMotionValueEvent, useScroll } from 'motion/react'
import { ReactNode, useState } from 'react'
import styles from './NavBar.module.css'

export interface RenderProps {
	isScrolled: boolean
	isUnscrolledImmersiveHeader: boolean
}
export interface NavBarProps extends Omit<HTMLMotionProps<'header'>, 'title' | 'children'> {
	/**
	 * @default false
	 */
	isImmersive?: boolean

	title?: ReactNode

	/**
	 * @default true
	 */
	showBackButton?: boolean

	renderRightSection?: (props: RenderProps) => ReactNode

	renderLeftSection?: (props: RenderProps) => ReactNode
}

export default function NavBar({
	isImmersive = false,
	title,
	showBackButton = true,
	renderRightSection,
	renderLeftSection,
	className,
	style,
	...props
}: NavBarProps) {
	const { scrollY } = useScroll()
	const [isScrolled, setIsScrolled] = useState<boolean>(false)
	const canGoBack = useCanGoBack()
	const isMobileShellHidden = useIsMobileShellHidden()
	const isMobileTabBarLayout = useIsMobileTabBarLayout()
	const isMobileNavBarHidden = isMobileTabBarLayout && isMobileShellHidden
	const isUnscrolledImmersiveHeader = isImmersive && !isScrolled

	const handleBack = () => {
		if (!canGoBack) {
			router.navigate({ to: '/following' })
		} else {
			router.history.back()
		}
	}

	useMotionValueEvent(scrollY, 'change', (val) => {
		setIsScrolled(val > 30)
	})

	return (
		<motion.header
			className={clsx(
				styles.header,
				isUnscrolledImmersiveHeader && styles.headerImmersive,
				className
			)}
			initial={false}
			animate={{
				y: isMobileNavBarHidden ? '-100%' : '0%',
			}}
			style={{
				...style,
				pointerEvents: isMobileNavBarHidden ? 'none' : style?.pointerEvents,
			}}
			transition={MOBILE_SHELL_SPRING_TRANSITION}
			{...props}
		>
			<div className={styles.leftSection}>
				{renderLeftSection?.({ isScrolled, isUnscrolledImmersiveHeader }) ??
					(showBackButton && (
						<Button
							className={styles.backButton}
							styles={{
								root: { paddingLeft: 0, paddingRight: 0, minWidth: 'unset', aspectRatio: '1' },
							}}
							variant={isUnscrolledImmersiveHeader ? 'glass' : 'transparent'}
							c={isUnscrolledImmersiveHeader ? 'var(--solid-white-1)' : 'var(--tint-secondary)'}
							size="sm"
							aria-label="返回"
							title="返回"
							onClick={handleBack}
						>
							<IconChevronLeft />
						</Button>
					))}
			</div>
			<div className={styles.title}>{title}</div>
			<div className={styles.rightSection}>
				{renderRightSection?.({ isScrolled, isUnscrolledImmersiveHeader })}
			</div>
		</motion.header>
	)
}
